import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import logo from "../../assets/img/reactlogo.png";

function ForgotPassword() {
  const navigate = useNavigate();

  // Steps: 1 = Mobile, 2 = OTP, 3 = Reset Password
  const [step, setStep] = useState(1);
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Step 1: Send OTP to Mobile
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setErrors({});
    if (!mobile || mobile.trim() === "") {
      setErrors({ mobile: "Mobile number is required" });
      return;
    }
    if (mobile.trim().length < 10) {
      setErrors({ mobile: "Please enter a valid mobile number" });
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post("/user/send/otp", { mobile: mobile.trim() });
      const { status, message } = response.data;
      if (status) {
        toast.success(message || "OTP sent successfully to your mobile number.");
        setStep(2);
      } else {
        toast.error(message || "Failed to send OTP.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "User not found or error sending OTP.");
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setErrors({});
    if (!otp || otp.trim() === "") {
      setErrors({ otp: "OTP is required" });
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post("/user/verify/otp", {
        mobile: mobile.trim(),
        otp: otp.trim(),
      });
      const { status, message } = response.data;
      if (status) {
        toast.success(message || "OTP matched successfully.");
        setStep(3);
      } else {
        toast.error(message || "Invalid OTP. Please try again.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Wrong OTP.");
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setErrors({});
    if (!password) {
      setErrors({ password: "New password is required" });
      return;
    }
    if (password.length < 6) {
      setErrors({ password: "Password must be at least 6 characters" });
      return;
    }
    if (password !== confirmPassword) {
      setErrors({ confirmPassword: "Passwords do not match" });
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post("/user/reset/new/password", {
        mobile: mobile.trim(),
        password: password,
      });
      const { status, message } = response.data;
      if (status) {
        toast.success(message || "Password changed successfully.");
        navigate("/login");
      } else {
        toast.error(message || "Failed to reset password.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to reset password.");
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP Helper
  const handleResendOtp = async () => {
    try {
      setLoading(true);
      const response = await axios.post("/user/send/otp", { mobile: mobile.trim() });
      const { status, message } = response.data;
      if (status) {
        toast.success(message || "OTP resent successfully.");
      } else {
        toast.error(message || "Failed to resend OTP.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Error resending OTP.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="col-lg-6 d-flex align-items-center justify-content-center">
      <div className="auth-form-transparent text-left p-3" style={{ maxWidth: "450px", width: "100%" }}>
        <div className="brand-logo mb-3">
          <img src={logo} alt="logo" />
        </div>
        <h4>Forgot Password?</h4>
        <h6 className="font-weight-light mb-3">
          {step === 1 && "Enter your registered mobile number to receive an OTP."}
          {step === 2 && `Enter the 6-digit OTP sent to +91 ${mobile}`}
          {step === 3 && "Create a new password for your account."}
        </h6>

        {/* Step Indicators */}
        <div className="d-flex mb-4 gap-2">
          <div
            className={`flex-grow-1 border-top border-3 ${step >= 1 ? "border-primary" : "border-secondary"}`}
          ></div>
          <div
            className={`flex-grow-1 border-top border-3 ${step >= 2 ? "border-primary" : "border-secondary"}`}
          ></div>
          <div
            className={`flex-grow-1 border-top border-3 ${step >= 3 ? "border-primary" : "border-secondary"}`}
          ></div>
        </div>

        {/* Step 1 Form: Mobile Number */}
        {step === 1 && (
          <form className="pt-2" onSubmit={handleSendOtp}>
            <div className="form-group mb-3">
              <label htmlFor="mobile">Registered Mobile Number</label>
              <div className="input-group">
                <div className="input-group-prepend bg-transparent">
                  <span className="input-group-text bg-transparent border-right-0">
                    <i className="ti-mobile text-primary"></i>
                  </span>
                </div>
                <input
                  type="text"
                  className="form-control form-control-lg border-left-0"
                  id="mobile"
                  name="mobile"
                  placeholder="Enter 10 digit Mobile Number"
                  value={mobile}
                  onChange={(e) => {
                    setMobile(e.target.value);
                    if (errors.mobile) setErrors({});
                  }}
                  required
                />
              </div>
              {errors.mobile && <span className="text-danger small">{errors.mobile}</span>}
            </div>

            <div className="my-3">
              <button
                type="submit"
                className="btn btn-block btn-primary btn-lg font-weight-medium auth-form-btn w-100"
                disabled={loading}
              >
                {loading ? "SENDING OTP..." : "SEND OTP"}
              </button>
            </div>
          </form>
        )}

        {/* Step 2 Form: Enter OTP */}
        {step === 2 && (
          <form className="pt-2" onSubmit={handleVerifyOtp}>
            <div className="form-group mb-3">
              <label htmlFor="otp">Enter Verification OTP</label>
              <div className="input-group">
                <div className="input-group-prepend bg-transparent">
                  <span className="input-group-text bg-transparent border-right-0">
                    <i className="ti-key text-primary"></i>
                  </span>
                </div>
                <input
                  type="text"
                  className="form-control form-control-lg border-left-0"
                  id="otp"
                  name="otp"
                  placeholder="Enter OTP"
                  value={otp}
                  onChange={(e) => {
                    setOtp(e.target.value);
                    if (errors.otp) setErrors({});
                  }}
                  required
                />
              </div>
              {errors.otp && <span className="text-danger small">{errors.otp}</span>}
            </div>

            <div className="d-flex justify-content-between align-items-center mb-3">
              <small className="text-muted">Didn't receive code?</small>
              <button
                type="button"
                className="btn btn-link p-0 text-primary small border-0 bg-transparent"
                onClick={handleResendOtp}
                disabled={loading}
              >
                Resend OTP
              </button>
            </div>

            <div className="my-3 d-flex gap-2">
              <button
                type="button"
                className="btn btn-secondary btn-lg font-weight-medium flex-grow-1"
                onClick={() => setStep(1)}
              >
                BACK
              </button>
              <button
                type="submit"
                className="btn btn-primary btn-lg font-weight-medium auth-form-btn flex-grow-1"
                disabled={loading}
              >
                {loading ? "VERIFYING..." : "VERIFY OTP"}
              </button>
            </div>
          </form>
        )}

        {/* Step 3 Form: Reset Password */}
        {step === 3 && (
          <form className="pt-2" onSubmit={handleResetPassword}>
            <div className="form-group mb-3">
              <label htmlFor="password">New Password</label>
              <div className="input-group">
                <div className="input-group-prepend bg-transparent">
                  <span className="input-group-text bg-transparent border-right-0">
                    <i className="ti-lock text-primary"></i>
                  </span>
                </div>
                <input
                  type="password"
                  className="form-control form-control-lg border-left-0"
                  id="password"
                  name="password"
                  placeholder="Enter New Password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errors.password) setErrors({});
                  }}
                  required
                />
              </div>
              {errors.password && <span className="text-danger small">{errors.password}</span>}
            </div>

            <div className="form-group mb-3">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <div className="input-group">
                <div className="input-group-prepend bg-transparent">
                  <span className="input-group-text bg-transparent border-right-0">
                    <i className="ti-lock text-primary"></i>
                  </span>
                </div>
                <input
                  type="password"
                  className="form-control form-control-lg border-left-0"
                  id="confirmPassword"
                  name="confirmPassword"
                  placeholder="Confirm New Password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (errors.confirmPassword) setErrors({});
                  }}
                  required
                />
              </div>
              {errors.confirmPassword && (
                <span className="text-danger small">{errors.confirmPassword}</span>
              )}
            </div>

            <div className="my-3">
              <button
                type="submit"
                className="btn btn-block btn-primary btn-lg font-weight-medium auth-form-btn w-100"
                disabled={loading}
              >
                {loading ? "RESETTING..." : "RESET PASSWORD"}
              </button>
            </div>
          </form>
        )}

        <div className="text-center mt-4 font-weight-light">
          Remembered your password?{" "}
          <Link to="/login" className="text-primary font-weight-bold">
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
